/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

/**
 * This component serves as a reusable footer for dialogs and forms.
 * It accepts two optional buttons via `ng-content`:
 * - `leftButton`: Placed on the left.
 * - `middleButton`: Positioned next to the right button.
 *
 * A third button (rightmost) is always present and configurable via inputs.
 * This buttons can make use of the formId input to work as a submit button for a form by its id:
 * <form [formGroup]="myForm" id="myFormId">...</form>
 * <button-footer [formId]="'myFormId'" ... ></button-footer>
 *
 * ### Usage Guidelines:
 * - Use `mat-button` for the left and middle buttons.
 * - Default button color is `"primary"`.
 * - Destructive actions (e.g., DELETE) should use `color="warn"` and be placed in the left position.
 * - The `<button-footer>` can be used with the `mat-dialog-actions` directive for proper Material styling.
 * - The [formId] input allows the right button to submit a form by its id.
 *
 * ### Example Usage:
 * ```html
 * <button-footer
 *   mat-dialog-actions
 *   (buttonSaveOrClose)="onSubmit()"
 *   [disabled]="informationForm.invalid"
 *   [saveOrCloseButtonText]="strings.SAVE_BTN"
 *   [formId]="'informationForm'"
 * >
 *   <button mat-button leftButton color="warn" (click)="onDelete()">
 *     {{ strings.DELETE_BTN }}
 *   </button>
 *   <button middleButton mat-button color="primary" (click)="onCancel()">
 *     {{ strings.CANCEL_BTN }}
 *   </button>
 * </button-footer>
 * ```
 */ @Component({
	selector: 'button-footer',
	templateUrl: './button-footer.component.html',
	styleUrls: ['./button-footer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonFooterComponent {
	@Input() public saveOrCloseButtonText: string;
	@Input() public disabled = false;
	@Input() public formId?: string;
	@Output() public buttonSaveOrClose = new EventEmitter();

	public saveOrClose(): void {
		this.buttonSaveOrClose.emit();
	}
}
